import { Auth } from 'aws-amplify';

/**
 * AuthManager - Gestor centralizado de autenticación
 * 
 * Responsabilidades:
 * - Unificar las 3 "vidas" de tokens (User Pool, Identity Pool, Impersonación)
 * - Deduplicar refresh de tokens con promise única
 * - Keep-alive preventivo para subidas largas
 * - Manejo inteligente de visibilidad/foco de pestañas
 * - Diferencia entre User Pool (API) e Identity Pool (S3/Storage)
 */
class AuthManager {
  constructor() {
    this.refreshPromise = null;
    this.isRefreshing = false;
    this.preventiveInterval = null;
    this.lastActivity = Date.now();
    this.setupVisibilityHandling();
  }

  /**
   * Asegura que tenemos credenciales frescas
   * @param {number} minValidityMs - Mínimo tiempo de validez requerido (default: 3 min)
   * @returns {Promise<{type: string, token: string, session?: object}>}
   */
  async ensureFreshCredentials(minValidityMs = 3 * 60 * 1000) {
    // Deduplicación: si ya hay un refresh en curso, reutilizar la promise
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    
    this.refreshPromise = this._doRefresh(minValidityMs);
    
    try {
      const result = await this.refreshPromise;
      return result;
    } catch (error) {
      console.error('[AuthManager] ❌ Refresh failed:', error);
      throw error;
    } finally {
      setTimeout(() => {
        this.refreshPromise = null;
      }, 100);
    }
  }

  /**
   * Ejecuta el refresh real de credenciales
   * @private
   */
  async _doRefresh(minValidityMs) {
    // 1. Prioridad: Token de impersonación
    const impersonationToken = localStorage.getItem('n30xp5jdk7a');
    
    if (impersonationToken) {
      const payload = this._parseJWT(impersonationToken);
      if (payload?.exp) {
        const timeUntilExpiry = payload.exp * 1000 - Date.now();
        if (timeUntilExpiry > minValidityMs) {
          return { 
            type: 'impersonation', 
            token: impersonationToken,
            expiresIn: timeUntilExpiry
          };
        } else {
          // Token de impersonación expirado
          localStorage.removeItem('n30xp5jdk7a');
          throw new Error('Impersonation token expired');
        }
      }
    }

    // 2. Fallback: Sesión de Cognito
    try {
      let session = await Auth.currentSession();
      const tokenExp = session.getIdToken().payload.exp * 1000;
      const timeUntilExpiry = tokenExp - Date.now();
      
      // Si el token expira pronto, forzar refresh
      if (timeUntilExpiry < minValidityMs) {
        session = await Auth.currentSession({ bypassCache: true });
      }
      
      return { 
        type: 'cognito', 
        token: session.getIdToken().getJwtToken(),
        session,
        expiresIn: session.getIdToken().payload.exp * 1000 - Date.now()
      };
    } catch (error) {
      console.error('[AuthManager] Failed to refresh credentials:', error);
      throw error;
    }
  }

  /**
   * Verifica y renueva credenciales del Identity Pool para S3/Storage
   * @returns {Promise<void>}
   */
  async ensureFreshStorageCredentials() {
    try {
      // Para Identity Pool, Amplify maneja automáticamente la renovación
      // pero podemos forzar un refresh del User Pool que activa el Identity Pool
      await this.ensureFreshCredentials(3 * 60 * 1000); // 3 min buffer para Storage
      
      // Opcional: verificar explícitamente credenciales AWS
      const credentials = await Auth.currentCredentials();
      if (!credentials || !credentials.accessKeyId) {
        throw new Error('No valid AWS credentials found');
      }
      
      // Solo las claves que Storage.put acepta como `credentials`
      return Auth.essentialCredentials(credentials);
    } catch (error) {
      console.error('[AuthManager] ❌ Storage credentials refresh failed:', error);
      throw error;
    }
  }

  /**
   * Inicia keep-alive preventivo para subidas largas
   * Renueva credenciales cada 3 minutos si quedan menos de 3 min
   */
  startUploadKeepAlive() {
    if (this.preventiveInterval) {
      this.stopUploadKeepAlive(); // Limpiar anterior si existe
    }

    this.preventiveInterval = setInterval(async () => {
      try {
        // ✅ Renovar tanto User Pool como Identity Pool
        await this.ensureFreshStorageCredentials();
      } catch (error) {
        console.error('[AuthManager] ❌ Keep-alive refresh failed:', error);
      }
    }, 3 * 60 * 1000); // Cada 3 minutos (más frecuente para Storage)
  }

  /**
   * Detiene el keep-alive preventivo
   */
  stopUploadKeepAlive() {
    if (this.preventiveInterval) {
      clearInterval(this.preventiveInterval);
      this.preventiveInterval = null;
    }
  }

  /**
   * Configura manejo inteligente de visibilidad y foco
   * Reemplaza el setInterval(1h) problemático
   */
  setupVisibilityHandling() {
    // Detectar cuando la pestaña vuelve a estar visible
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const timeSinceHidden = Date.now() - this.lastActivity;
        if (timeSinceHidden > 3 * 60 * 1000) { // >3 min oculta
          this.ensureFreshCredentials().catch(error => {
            console.error('[AuthManager] Visibility refresh failed:', error);
          });
        }
      } else {
        this.lastActivity = Date.now();
      }
    };

    // Detectar cuando la ventana vuelve a tener foco
    const handleWindowFocus = () => {
      this.ensureFreshCredentials().catch(error => {
        console.error('[AuthManager] Focus refresh failed:', error);
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    
    // Cleanup en caso de que se llame múltiples veces
    this._cleanupVisibilityHandlers = () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }

  /**
   * Verifica si el usuario está autenticado
   * @returns {Promise<boolean>}
   */
  async isAuthenticated() {
    try {
      await this.ensureFreshCredentials();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Limpieza al destruir el manager
   */
  destroy() {
    this.stopUploadKeepAlive();
    if (this._cleanupVisibilityHandlers) {
      this._cleanupVisibilityHandlers();
    }
  }

  /**
   * Parsea un JWT token
   * @private
   */
  _parseJWT(token) {
    try {
      const base64Payload = token.split('.')[1];
      const payload = JSON.parse(atob(base64Payload));
      return payload;
    } catch (e) {
      return null;
    }
  }
}

// Singleton instance
export default new AuthManager();
